import React, { useState, useEffect, useCallback } from "react";
import Header from "../../commons/Header/Header";
import Sidebar from "../../commons/Sidebar/Sidebar";
import Img from "../../assets/images/img.webp";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { toast } from "react-toastify";
import { Row, Col } from "react-bootstrap";
import { UI_CONSTANTS } from "../../utils/constants";
import {
  LIST_RETURN_KEYS,
  persistListReturn,
  consumeListReturn,
} from "../../utils/listNavigation";

const PAYMENT_STATUS_LABELS = {
  0: "Unpaid",
  1: "Paid",
  2: "Refunded",
};

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatAmount = (transaction) => {
  const amount = transaction?.amount ?? transaction?.payment ?? transaction?.price;
  if (amount === undefined || amount === null || amount === "") return "N/A";
  const currency = String(transaction?.currency || "gbp").toUpperCase();
  const symbol = currency === "GBP" ? "£" : currency === "USD" ? "$" : currency === "EUR" ? "€" : "";
  return `${symbol}${amount}`;
};

const formatBillingCycle = (transaction) => {
  const cycle =
    transaction?.subscription_plan_id?.billing_cycle || transaction?.billing_cycle;
  if (!cycle) return "N/A";
  if (cycle === "one_time") {
    const months =
      transaction?.subscription_plan_id?.plan_duration_months ||
      transaction?.plan_duration_months;
    return months ? `${months} Month${months > 1 ? "s" : ""}` : "One Time";
  }
  return String(cycle)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getPaymentStatusLabel = (status) => {
  if (status === undefined || status === null || status === "") return "N/A";
  const numeric = Number(status);
  if (!Number.isNaN(numeric) && PAYMENT_STATUS_LABELS[numeric]) {
    return PAYMENT_STATUS_LABELS[numeric];
  }
  return String(status);
};

const getSubscriptionStatusLabel = (status) => {
  if (!status && status !== 0) return "N/A";
  const normalized = String(status).toUpperCase();
  const labels = {
    PENDING: "Pending",
    ACTIVE: "Active",
    EXHAUSTED: "Exhausted",
    EXPIRED: "Expired",
    CANCELLED: "Cancelled",
    CANCELED: "Cancelled",
  };
  return labels[normalized] || String(status);
};

const getPlanType = (transaction) => {
  if (transaction?.plan_type) return transaction.plan_type;
  if (transaction?.subscription_plan_id?.is_recurring === true) return "Recurring";
  if (transaction?.subscription_plan_id?.is_recurring === false) return "One Time";
  if (transaction?.subscription_plan_id?.billing_cycle === "one_time") return "One Time";
  return "N/A";
};

const extractTransaction = (payload) => {
  if (!payload) return null;
  if (payload.transaction && typeof payload.transaction === "object" && !Array.isArray(payload.transaction)) {
    return payload.transaction;
  }
  if (payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
    return payload.data;
  }
  if (typeof payload === "object" && !Array.isArray(payload)) {
    return payload;
  }
  return null;
};

const renderFieldCard = (label, value) => (
  <div className="col-md-4 p-2" key={label}>
    <div className="rounded-14 border p-3">
      <strong>{label}</strong>
      <p className="mb-0 mt-2">{value ?? "N/A"}</p>
    </div>
  </div>
);

function SubscriptionView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [transaction, setTransaction] = useState(null);

  useEffect(() => {
    if (location.state?.from || location.state?.listState) {
      persistListReturn(
        LIST_RETURN_KEYS.TRANSACTION.from,
        LIST_RETURN_KEYS.TRANSACTION.state,
        location.state?.from,
        location.state?.listState
      );
    }
  }, [location.state]);

  const getTransactionDetail = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await handleApiCall("get", `${endpoints.getViewTransaction}/${id}`);

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        setTransaction(extractTransaction(response?.data?.data));
      } else {
        setTransaction(null);
        if (response?.data?.message) {
          toast.error(response.data.message);
        }
      }
    } catch (error) {
      console.error("Error fetching transaction details:", error);
      setTransaction(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    getTransactionDetail();
  }, [getTransactionDetail]);

  const handleBack = () => {
    const { from, listState } = consumeListReturn(
      LIST_RETURN_KEYS.TRANSACTION.from,
      LIST_RETURN_KEYS.TRANSACTION.state,
      location.state
    );

    navigate(from || "/transaction-management", {
      state: listState ? { listState } : undefined,
    });
  };

  const restaurant = transaction?.restaurant_id;
  const plan = transaction?.subscription_plan_id;
  const includedFeatures =
    plan?.features ||
    plan?.included ||
    transaction?.features ||
    transaction?.subscription_included ||
    [];

  if (loading) {
    return (
      <div className="container-scroller">
        <Header />
        <div className="container-fluid page-body-wrapper">
          <Sidebar />
          <div className="main-panel">
            <div className="content-wrapper text-center py-5">
              <div className="spinner-border" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-3 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">Subscription View</h3>
            </div>
            <div>
              <div className="mt-3 d-flex">
                <button type="button" className="btn btn-default rounded-lg" onClick={handleBack}>
                  Back
                </button>
              </div>
            </div>

            {!transaction ? (
              <div className="text-center py-5">
                <p>{UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}</p>
              </div>
            ) : (
              <>
                <div className="border rounded-14 py-2 px-3 mt-5">
                  <Row className="align-items-center">
                    <Col lg="1" className="pr-0">
                      <div className="imgCard" style={{ height: "100px" }}>
                        <img
                          src={restaurant?.image || restaurant?.logo || Img}
                          alt={restaurant?.restaurant_name || "Restaurant"}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = Img;
                          }}
                        />
                      </div>
                    </Col>
                    <Col lg="6">
                      <h4 className="mb-1">{restaurant?.restaurant_name || "N/A"}</h4>
                      <div>
                        {restaurant?.full_name
                          ? `Owner: ${restaurant.full_name}`
                          : restaurant?.email || "N/A"}
                      </div>
                      {(restaurant?.phone || restaurant?.email) && (
                        <div className="mt-1">
                          {restaurant?.phone ? `+${restaurant.phone}` : ""}
                          {restaurant?.phone && restaurant?.email ? " | " : ""}
                          {restaurant?.email || ""}
                        </div>
                      )}
                    </Col>
                  </Row>

                  <div className="row mx-n2 mt-3">
                    {renderFieldCard("Transaction ID", `#${transaction._id?.slice(-8) || "N/A"}`)}
                    {renderFieldCard(
                      "Subscription",
                      transaction.plan_name || plan?.name || "N/A"
                    )}
                    {renderFieldCard("Plan Type", getPlanType(transaction))}
                    {renderFieldCard("Amount", formatAmount(transaction))}
                    {renderFieldCard("Billing Cycle", formatBillingCycle(transaction))}
                    {renderFieldCard("Payment Status", getPaymentStatusLabel(transaction.payment_status))}
                    {renderFieldCard(
                      "Subscription Status",
                      getSubscriptionStatusLabel(transaction.subscription_status)
                    )}
                    {renderFieldCard("Start Date", formatDate(transaction.start_date))}
                    {renderFieldCard(
                      "End Date",
                      formatDate(transaction.expiry_date || transaction.end_date)
                    )}
                    {renderFieldCard(
                      "Purchased Call Limit",
                      transaction.purchased_call_limit ?? plan?.call_usage_limit ?? "N/A"
                    )}
                    {renderFieldCard(
                      "Used Call Limit",
                      transaction.used_call_limit ?? "N/A"
                    )}
                    {renderFieldCard(
                      "Remaining Call Limit",
                      transaction.remaining_call_limit ?? "N/A"
                    )}
                    {renderFieldCard(
                      "Payment Method",
                      transaction.payment_method
                        ? String(transaction.payment_method).replace(/\b\w/g, (c) => c.toUpperCase())
                        : "N/A"
                    )}
                    {renderFieldCard("Paid At", formatDate(transaction.paid_at))}
                  </div>
                </div>

                {Array.isArray(includedFeatures) && includedFeatures.length > 0 && (
                  <div className="mt-4 px-3">
                    <h5 className="mb-1">SUBSCRIPTION INCLUDED</h5>
                    <ul>
                      {includedFeatures.map((feature, index) => (
                        <li key={index}>
                          {typeof feature === "string"
                            ? feature
                            : feature?.name || feature?.title || JSON.stringify(feature)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionView;
